import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Object3D } from '@wonderlandengine/api';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { CameraController } from './camera-controller.js';
import { PaintConfigurator } from './configurator/paint-configurator.js';
import { WheelConfigurator } from './configurator/wheel-configurator.js';
import { InteriorConfigurator } from './configurator/interior-configurator.js';
import { ExteriorConfigurator } from './configurator/exterior-configurator.js';
import { AccessoriesConfigurator } from './configurator/accessories-configurator.js';
import { SubModelConfigurator } from './configurator/sub-model-configurator.js';
import { Configuration } from './data/configuration.js';
import { CloudData } from './data/cloud-data.js';
import { WebService } from './services/web-service.js';
import { App } from './components/app.js';

/**
 * Scene Controller:
 */
export class SceneController extends Component {
	static TypeName = 'scene-controller';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	cameraController!: Object3D;

	@property.object({ required: true })
	configurator!: Object3D;

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	async start() {

		// detect the platform (mobile or desktop), and set the configuration flag and body class accordingly.
		const isMobile = matchMedia('(pointer: coarse)').matches || /iPhone|iPad|Android/i.test(navigator.userAgent);
		if (!isMobile) {
			document.body.className = 'desktop';
			Configuration.platformIsMobile = false;
		} else {
			document.body.className = 'mobile';
			Configuration.platformIsMobile = true;
		}

		// get components.
		const cameraController = this.cameraController.getComponent(CameraController)!;
		const paintConfigurator = this.configurator.getComponent(PaintConfigurator)!;
		const wheelConfigurator = this.configurator.getComponent(WheelConfigurator)!;
		const interiorConfigurator = this.configurator.getComponent(InteriorConfigurator)!;
		const exteriorConfigurator = this.configurator.getComponent(ExteriorConfigurator)!;
		const accessoriesConfigurator = this.configurator.getComponent(AccessoriesConfigurator)!;
		const subModelConfigurator = this.configurator.getComponent(SubModelConfigurator)!;

		// set the region from querystring params (if any) - n.b. country is a legacy alias for region.
		const queryString = new URLSearchParams(location.search.toLowerCase());
		const regionCode = queryString.get('region') ?? queryString.get('country');
		if (regionCode) {
			Configuration.regionCode = regionCode.toLowerCase();
		}

		// otherwise, auto-detect the region from the user's IP, applying it only if it is a supported country.
		else {
			const detectedRegionCode = await WebService.detectRegionCode();
			if (detectedRegionCode && CloudData.isSupportedRegion(detectedRegionCode)) {
				Configuration.regionCode = detectedRegionCode;
			}
		}

		// set the language code from querystring params (if any).
		const languageCode = queryString.get('language');
		if (languageCode) {
			Configuration.languageCode = languageCode.toLowerCase();
		}

		// allow model to be set from querystring (for development).
		const model = queryString.get('model');
		if (model) {
			Configuration.modelId = model;

			// reset configuration to ensure defaults are correct for the model.
			Configuration.resetConfiguration();
		}

		// render the React app, passing in the components.
		const root = createRoot(document.querySelector('#root') as HTMLElement);
		root.render(
			<App
				cameraController={cameraController}
				paintConfigurator={paintConfigurator}
				wheelConfigurator={wheelConfigurator}
				interiorConfigurator={interiorConfigurator}
				exteriorConfigurator={exteriorConfigurator}
				accessoriesConfigurator={accessoriesConfigurator}
				subModelConfigurator={subModelConfigurator}
				isMobile={isMobile}
			/>
		);

		// n.b. put the above code in a timeout to simulate the app loading!!
		//	setTimeout(() => {
		//	}, 5000);
	}
}
